import { Lock, Wallet, PiggyBank, Shield, TrendingUp, Building2, BarChart3, ArrowRightLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Account } from "@/hooks/useAccounts";

const ACCOUNT_ICONS: Record<string, React.ElementType> = {
  checking: Wallet,
  savings: PiggyBank,
  emergency_fund: Shield,
  roth_ira: TrendingUp,
  "401k": Building2,
  investment: BarChart3,
};

const ACCOUNT_COLORS: Record<string, string> = {
  checking: "from-primary/20 to-primary/5 border-primary/30",
  savings: "from-secondary/20 to-secondary/5 border-secondary/30",
  emergency_fund: "from-accent/20 to-accent/5 border-accent/30",
  roth_ira: "from-primary/20 to-secondary/5 border-primary/30",
  "401k": "from-secondary/20 to-primary/5 border-secondary/30",
  investment: "from-accent/20 to-primary/5 border-accent/30",
};

interface AccountCardProps {
  account: Account;
  onTransfer?: () => void;
}

const AccountCard = ({ account, onTransfer }: AccountCardProps) => {
  const Icon = ACCOUNT_ICONS[account.account_type] || Wallet;
  const isLocked = account.status === "locked";
  const colorClasses = ACCOUNT_COLORS[account.account_type] || ACCOUNT_COLORS.checking;

  return (
    <div
      className={`relative rounded-2xl border bg-gradient-to-br transition-all duration-300 ${
        isLocked
          ? "border-border/50 bg-muted/30 opacity-60"
          : `${colorClasses} hover:shadow-glow`
      }`}
    >
      <div className="p-4 space-y-3">
        {/* Row 1: Icon + Name + Lock badge */}
        <div className="flex items-center gap-3">
          <div
            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
              isLocked ? "bg-muted" : "bg-card/80 shadow-sm"
            }`}
          >
            <Icon className={`w-4 h-4 ${isLocked ? "text-muted-foreground" : "text-primary"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-sm text-foreground truncate">{account.account_name}</h3>
          </div>
          {isLocked && (
            <div className="shrink-0 flex items-center gap-1 rounded-md bg-muted/60 border border-border/50 px-2 py-0.5">
              <Lock className="w-3 h-3 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground">Locked</span>
            </div>
          )}
        </div>

        {/* Row 2: Subtitle or unlock condition */}
        <p className="text-xs text-muted-foreground line-clamp-2 break-words">
          {isLocked ? account.unlock_condition : account.subtitle}
        </p>

        {/* Row 3: Balance */}
        <div>
          {isLocked ? (
            <p className="font-heading font-bold text-lg text-muted-foreground/50">—</p>
          ) : (
            <p className="font-heading font-bold text-xl text-foreground">
              ${account.balance.toLocaleString()}
            </p>
          )}
        </div>

        {/* Row 4: Action */}
        {!isLocked && onTransfer && (
          <Button
            variant="outline"
            size="sm"
            onClick={onTransfer}
            className="w-full text-xs gap-1.5 border-border/60 hover:bg-card/60"
          >
            <ArrowRightLeft className="w-3 h-3" />
            Transfer
          </Button>
        )}
      </div>
    </div>
  );
};

export default AccountCard;
